import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CircleCheckBig, CircleDashed, ChevronDown, X } from 'lucide-react'
import type { OnboardingStepKey } from '@/api/onboarding'
import { useOnboarding } from '@/hooks/useOnboarding'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'
import { ExploreSampleLink } from '@/components/sample/ExploreSampleLink'
import { ROUTES } from '@/routes/routes'
import { cn } from '@/lib/utils'

const STEP_ROUTE: Record<OnboardingStepKey, string> = {
  add_account: ROUTES.APP.ACCOUNTS,
  log_transaction: ROUTES.APP.TRANSACTIONS,
  set_budget: ROUTES.APP.BUDGET,
  invite_family: ROUTES.APP.SETTINGS_MEMBERS,
}

/** A single unobtrusive line under the Dashboard header; expands to the step list on click. */
export function OnboardingChecklist() {
  const { data, isLoading, isError, skipStep, dismissAll, isUpdating } = useOnboarding()
  const { isSample } = useCurrentHousehold()
  const [open, setOpen] = useState(false)

  if (isLoading || isError || !data || data.dismissed) return null

  const remaining = data.steps.filter((s) => !s.done && !s.skipped)
  if (remaining.length === 0) return null

  const doneCount = data.steps.filter((s) => s.done).length

  return (
    <section className="mb-6 rounded-lg border border-[var(--fl-border)] bg-[var(--fl-surface)]">
      <div className="flex items-center gap-2 px-4 py-2.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm"
        >
          <span className="font-medium text-[var(--fl-text-1)]">
            Finish setting up your household
          </span>
          <span className="shrink-0 text-[var(--fl-text-3)]">
            {doneCount} of {data.steps.length}
          </span>
          <ChevronDown
            size={14}
            className={cn(
              'shrink-0 text-[var(--fl-text-3)] transition-transform',
              open && 'rotate-180'
            )}
          />
        </button>
        <button
          type="button"
          onClick={dismissAll}
          disabled={isUpdating}
          aria-label="Dismiss setup checklist"
          className="shrink-0 rounded p-1 text-[var(--fl-text-3)] transition-colors hover:text-[var(--fl-text-1)]"
        >
          <X size={14} />
        </button>
      </div>

      {!isSample && (
        <div className="px-4 pb-2.5">
          <ExploreSampleLink lead="Want to see how it looks first?" label="Explore sample data" />
        </div>
      )}

      {open && (
        <ul className="border-t border-[var(--fl-divider)] px-4 py-1">
          {data.steps.map((step) => {
            const resolved = step.done || step.skipped
            return (
              <li key={step.key} className="flex items-center gap-2.5 py-1.5 text-sm">
                {step.done ? (
                  <CircleCheckBig size={15} className="shrink-0 text-[var(--fl-income)]" />
                ) : (
                  <CircleDashed size={15} className="shrink-0 text-[var(--fl-text-3)]" />
                )}
                {resolved ? (
                  <span
                    className={cn(
                      'flex-1',
                      step.done ? 'text-[var(--fl-text-3)] line-through' : 'text-[var(--fl-text-3)]'
                    )}
                  >
                    {step.title}
                    {step.skipped && ' · skipped'}
                  </span>
                ) : (
                  <Link
                    to={STEP_ROUTE[step.key]}
                    className="flex-1 text-[var(--fl-text-1)] hover:text-[var(--fl-primary-text)]"
                  >
                    {step.title}
                  </Link>
                )}
                {!resolved && (
                  <button
                    type="button"
                    onClick={() => skipStep(step.key)}
                    disabled={isUpdating}
                    className="shrink-0 text-xs text-[var(--fl-text-3)] transition-colors hover:text-[var(--fl-text-1)]"
                  >
                    Skip
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
